import { useEffect, useId, useState, type ReactNode } from 'react';
import {
  ArrowUpRight,
  Coffee,
  Cpu,
  Gpu,
  GraduationCap,
  Hand,
  LockKeyhole,
  MemoryStick,
  Moon,
} from 'lucide-react';
import { countSkills, projectLearningSkills } from '../shared/learning-skills';
import {
  speedAdvice,
  type AdviceActionId,
  type AdviceRow,
  type AdviceRowId,
  type SpeedAdviceFacts,
} from './speed-advice';

const ICONS: Record<AdviceRowId, ReactNode> = {
  card: <Gpu size={18} aria-hidden="true" />,
  processor: <Cpu size={18} aria-hidden="true" />,
  memory: <MemoryStick size={18} aria-hidden="true" />,
  teach: <GraduationCap size={18} aria-hidden="true" />,
  train: <Moon size={18} aria-hidden="true" />,
  'free-memory': <Gpu size={18} aria-hidden="true" />,
  away: <Coffee size={18} aria-hidden="true" />,
};

/** How long a skill count waits before reading again while the engine is still counting page designs. */
const SKILL_COUNT_RETRY_MS = 5_000;

/** One row. Its web link opens in the browser: the main window hands every https window to it. */
function Row({ row, onAction }: { row: AdviceRow; onAction: (id: AdviceActionId) => void }) {
  return (
    <li className="speed-advice-row" data-advice={row.id}>
      <span className="speed-advice-icon">{ICONS[row.id]}</span>
      <div className="speed-advice-copy">
        <strong>
          <span>{row.name}</span>
          {row.value && <span className={`speed-advice-value ${row.tone}`}>{row.value}</span>}
        </strong>
        {row.line && (
          <p>
            {row.line}
            {row.link && (
              <>
                {' '}
                <a
                  className="speed-advice-link"
                  href={row.link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {row.link.label}
                  <ArrowUpRight size={12} aria-hidden="true" />
                </a>
              </>
            )}
          </p>
        )}
      </div>
      {row.action && (
        <button
          type="button"
          className="secondary-button small speed-advice-action"
          onClick={() => onAction(row.action!.id)}
        >
          {row.action.label}
        </button>
      )}
    </li>
  );
}

/**
 * Two sides under the speed test's score (owner decision, October 6, 2026): what this
 * computer's hardware sets, beside what is up to the person, each with this computer's or
 * this job's own value (speed-advice.ts). Shown for every result; a test in progress shows
 * its progress instead.
 */
export function SpeedAdvice({
  facts,
  onAction,
}: {
  facts: SpeedAdviceFacts;
  onAction: (id: AdviceActionId) => void;
}) {
  const id = useId();
  const advice = speedAdvice(facts);
  return (
    <div className="speed-advice">
      <section className="speed-advice-side" aria-labelledby={`${id}-computer`}>
        <h3 id={`${id}-computer`}>
          <LockKeyhole size={15} aria-hidden="true" />
          Set by this computer
        </h3>
        <ul>
          {advice.computer.map((row) => (
            <Row key={row.id} row={row} onAction={onAction} />
          ))}
        </ul>
      </section>
      <section className="speed-advice-side yours" aria-labelledby={`${id}-yours`}>
        <h3 id={`${id}-yours`}>
          <Hand size={15} aria-hidden="true" />
          Up to you
        </h3>
        <ul>
          {advice.yours.map((row) => (
            <Row key={row.id} row={row} onAction={onAction} />
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * How many of a job's skills are Ready, counted exactly as Learning counts them (the same
 * engine reads and projection), so the two screens never disagree. Null until read, or when
 * a read fails: the row then shows no count rather than a guess. A skill whose state was not
 * reported counts as neither, as in Learning, and keeps "Teach in Documents" offered.
 * While the engine is still counting its page designs, those skills are unknown; the counts
 * are read once more a few seconds later so the row does not keep the placeholder.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function useJobSkillCounts(jobId: string | null): SpeedAdviceFacts['skills'] {
  const [counts, setCounts] = useState<{
    jobId: string;
    ready: number;
    total: number;
    unknown: number;
  } | null>(null);
  useEffect(() => {
    const api = window.capturePointDesktop;
    if (!jobId || !api) return;
    let live = true;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const read = async (again: boolean) => {
      try {
        const [jobs, eligibility, report, fieldResults, tableResults, batch] = await Promise.all([
          Promise.resolve().then(() => api.listJobs()),
          Promise.resolve().then(() => api.learningEligibility(jobId)),
          Promise.resolve().then(() => api.getLearningAcceleration(jobId)),
          Promise.resolve().then(() => api.learningFieldResults(jobId)),
          Promise.resolve().then(() => api.learningTableResults(jobId)),
          Promise.resolve().then(() => api.getLatestBatch(jobId)),
        ]);
        const job = jobs.find((entry) => entry.id === jobId);
        if (!live || !job) return;
        const skills = countSkills(
          projectLearningSkills({
            job,
            eligibility,
            report,
            fieldResults,
            tableResults,
            documents: batch?.documents ?? null,
          }),
        );
        setCounts({ jobId, ready: skills.ready, total: skills.total, unknown: skills.unknown });
        if (again && report?.layouts?.summaryPending === true)
          retry = setTimeout(() => void read(false), SKILL_COUNT_RETRY_MS);
      } catch {
        /* No count is shown rather than a guess. */
      }
    };
    void read(true);
    return () => {
      live = false;
      if (retry) clearTimeout(retry);
    };
  }, [jobId]);
  return counts && counts.jobId === jobId
    ? { ready: counts.ready, total: counts.total, unknown: counts.unknown }
    : null;
}
